import { useCallback, useMemo, useRef } from "react";
import { Polyline, Tooltip } from "react-leaflet";
import type { Layer } from "leaflet";

import { useHaulRoads } from "./useHaulRoads";
import { useMapZoom } from "./useMapZoom";
import { StackedGroup } from "./LayerStack";
import { LAYER_RANK } from "./layerStackOrder";
import { roadCasingPathOptions, roadPathOptions } from "./mapStyles";

/**
 * Draws the haul roads between the geofences, styled by road class.
 *
 * Note: this is what turns a scatter of shapes into a mine. Without it the map shows fences and vehicles
 * and nothing connecting them, so there is no way to read where a truck came from or is heading - the
 * geometry of the site is entirely implied by where the markers happen to be.
 *
 * Note: every casing is drawn before any road, so where two haul roads meet they read as one surface
 * rather than one road with the other's edge drawn across it.
 */

type HaulRoadLayerProps = {
  visible: boolean;
};

export const HaulRoadLayer: React.FC<HaulRoadLayerProps> = ({ visible }) => {
  const { roads } = useHaulRoads();
  const zoom = useMapZoom();
  const layers = useRef({ casing: new Map<number, Layer>(), surface: new Map<number, Layer>() });

  const styled = useMemo(
    () => roads.map(road => ({
      road,
      pathOptions: roadPathOptions(road.roadClass, zoom),
      casingOptions: roadCasingPathOptions(road.roadClass, zoom),
    })),
    [roads, zoom]);

  const drawOrder = useCallback(
    () => (["casing", "surface"] as const).flatMap(part => roads.flatMap(road => layers.current[part].get(road.id) ?? [])),
    [roads]);

  const track = (part: "casing" | "surface", id: number) => (layer: Layer | null) => {
    if (layer) layers.current[part].set(id, layer);
    else layers.current[part].delete(id);
  };

  if (!visible) return null;

  return (
    <StackedGroup rank={LAYER_RANK.roads} version={roads} drawOrder={drawOrder}>
      {styled.map(({ road, casingOptions }) => casingOptions && (
        <Polyline
          key={`haul-road-casing-${road.id}`}
          ref={track("casing", road.id)}
          positions={road.path}
          pathOptions={casingOptions}
          interactive={false}
        />
      ))}
      {styled.map(({ road, pathOptions }) => (
        <Polyline key={`haul-road-${road.id}`} ref={track("surface", road.id)} positions={road.path} pathOptions={pathOptions}>
          <Tooltip>
            {road.name}
            {road.roadClass ? ` - ${road.roadClass}` : ""}
            {road.lengthMetres != null ? `, ${road.lengthMetres} m` : ""}
            {road.gradePercent != null ? `, ${road.gradePercent > 0 ? "+" : ""}${road.gradePercent}%` : ""}
          </Tooltip>
        </Polyline>
      ))}
    </StackedGroup>
  );
};

export default HaulRoadLayer;
